import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { Trash2, Eraser, Send, CheckCircle2, Loader2, ArrowLeft, PlayCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CANVAS_W = 900;
const CANVAS_H = 500;

const TIPI_SPAZIO_GROUPS = [
  {
    group: "Spazi già integrati nell'edificio",
    items: [
      { label: "Balcone", prezzo: 430 },
      { label: "Terrazzo", prezzo: 430 },
      { label: "Loggia", prezzo: 440 },
      { label: "Porticato / portico", prezzo: 450 },
    ],
  },
  {
    group: "Strutture esterne esistenti",
    items: [
      { label: "Pergola", prezzo: 455 },
      { label: "Pergola bioclimatica", prezzo: 460 },
      { label: "Tettoia", prezzo: 440 },
      { label: "Pensilina", prezzo: 440 },
      { label: "Patio coperto", prezzo: 450 },
    ],
  },
  {
    group: "Situazioni particolari",
    items: [
      { label: "Veranda esistente da sostituire/riqualificare", prezzo: 450 },
      { label: "Altro (da valutare)", prezzo: 465 },
    ],
  },
];

const PREZZI_SPAZIO = TIPI_SPAZIO_GROUPS.reduce((acc, g) => {
  g.items.forEach((i) => {
    acc[i.label] = i.prezzo;
  });
  return acc;
}, {});

const DETTAGLI_TECNICI = [  {
    key: "tipo_copertura",
    label: "Tipo di copertura — Cosa c'è sopra lo spazio da chiudere?",
    options: ["Nessuna copertura", "Balcone superiore", "Tettoia", "Pergola", "Pergola bioclimatica", "Portico", "Altro"],
  },
  {
    key: "materiale_struttura",
    label: "Materiale della struttura — Su quale struttura verrà installata la vetrata?",
    options: ["Muratura", "Cemento armato", "Ferro", "Alluminio", "Legno", "Non lo so"],
  },
  {
    key: "ostacoli",
    label: "Ostacoli presenti — Sono presenti elementi che potrebbero interferire con l'installazione?",
    options: ["Pluviali", "Colonne", "Pilastri", "Cassonetti", "Tende da sole", "Climatizzatore", "Nessuno"],
  },
  {
    key: "accessibilita",
    label: "Accessibilità — Come si raggiunge il piano?",
    options: ["Ascensore", "Scale", "Piattaforma", "Gru disponibile", "Da verificare"],
  },
  {
    key: "esposizione",
    label: "Esposizione — Lo spazio è esposto prevalentemente a:",
    options: ["Nord", "Sud", "Est", "Ovest"],
  },
  {
    key: "tipo_pavimento",
    label: "Tipo di pavimento",
    options: ["Gres", "Marmo", "Pietra", "Legno", "Resina", "Altro"],
  },
  {
    key: "stato_pavimentazione",
    label: "Stato della pavimentazione — Il pavimento è perfettamente in piano?",
    options: ["Sì", "Presenta una lieve pendenza", "Non lo so"],
  },
];

export default function Configuratore() {
  const [step, setStep] = useState(1);
  const [sent, setSent] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [preventivoNr, setPreventivoNr] = useState("");
  const [sending, setSending] = useState(false);
  const [openingConf, setOpeningConf] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const emailRef = useRef(null);

  // Step 1
  const [lead, setLead] = useState({ ragione_sociale: "", email: "", telefono: "", note: "" });

  // Step 2
  const [tipoSpazio, setTipoSpazio] = useState("");
  const [piano, setPiano] = useState("");
  const [numeroVani, setNumeroVani] = useState(1);
  const [vani, setVani] = useState([{ numero: 1, lunghezza: "", altezza: "" }]);
  const [dettagli, setDettagli] = useState({
    tipo_copertura: "",
    materiale_struttura: "",
    ostacoli: "",
    accessibilita: "",
    esposizione: "",
    tipo_pavimento: "",
    stato_pavimentazione: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Keep vani array in sync with numeroVani
  useEffect(() => {
    setVani((prev) => {
      const n = Math.max(1, Math.min(50, parseInt(numeroVani, 10) || 1));
      const next = [];
      for (let i = 0; i < n; i++) {
        next.push(prev[i] || { numero: i + 1, lunghezza: "", altezza: "" });
      }
      return next.map((v, i) => ({ ...v, numero: i + 1 }));
    });
  }, [numeroVani]);

  // ---------- CANVAS (ported from legacy JS) ----------
  const canvasRef = useRef(null);
  const objectsRef = useRef([]);
  const selectedRef = useRef(null);
  const selectedIndexRef = useRef(-1);
  const dragModeRef = useRef(null);
  const deleteSelectedRef = useRef(() => {});
  const clearCanvasRef = useRef(() => {});
  const addElementRef = useRef(() => {});

  useEffect(() => {
    if (step !== 2) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    function distance(x1, y1, x2, y2) {
      return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
    }
    function distancePointToLine(px, py, l) {
      const A = px - l.x1;
      const B = py - l.y1;
      const C = l.x2 - l.x1;
      const D = l.y2 - l.y1;
      const dot = A * C + B * D;
      const len_sq = C * C + D * D;
      const param = len_sq !== 0 ? dot / len_sq : -1;
      let xx, yy;
      if (param < 0) {
        xx = l.x1;
        yy = l.y1;
      } else if (param > 1) {
        xx = l.x2;
        yy = l.y2;
      } else {
        xx = l.x1 + param * C;
        yy = l.y1 + param * D;
      }
      return distance(px, py, xx, yy);
    }
    function drawGrid() {
      const stepGrid = 20;
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = "#e0e0e0";
      ctx.lineWidth = 1;
      for (let x = 0; x <= canvas.width; x += stepGrid) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
      }
      for (let y = 0; y <= canvas.height; y += stepGrid) {
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
      }
      ctx.stroke();
      ctx.restore();
    }
    function redraw() {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      drawGrid();
      ctx.font = "14px Arial";
      for (const o of objectsRef.current) {
        if (o === selectedRef.current) {
          ctx.lineWidth = 5;
          ctx.strokeStyle = "red";
        } else {
          ctx.lineWidth = 3;
          ctx.strokeStyle = o.color || "#000";
        }
        if (o.type === "line") {
          ctx.beginPath();
          ctx.moveTo(o.x1, o.y1);
          ctx.lineTo(o.x2, o.y2);
          ctx.stroke();
          if (o === selectedRef.current) {
            ctx.fillStyle = "red";
            ctx.beginPath();
            ctx.arc(o.x1, o.y1, 4, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(o.x2, o.y2, 4, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        if (o.type === "text") {
          ctx.fillStyle = o === selectedRef.current ? "red" : "#000";
          ctx.fillText(o.text, o.x, o.y);
        }
      }
    }
    clearCanvasRef.current = () => {
      objectsRef.current = [];
      selectedRef.current = null;
      selectedIndexRef.current = -1;
      redraw();
    };
    deleteSelectedRef.current = () => {
      if (selectedIndexRef.current === -1) {
        toast.error("Seleziona prima un elemento");
        return;
      }
      objectsRef.current.splice(selectedIndexRef.current, 1);
      selectedRef.current = null;
      selectedIndexRef.current = -1;
      redraw();
    };

    addElementRef.current = (type) => {
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      if (type === "vetrata" || type === "muro") {
        objectsRef.current.push({
          type: "line",
          x1: cx - 30,
          y1: cy,
          x2: cx + 30,
          y2: cy,
          color: type === "vetrata" ? "#00bfff" : "#8b5a2b",
        });
      } else if (type.startsWith("vano|")) {
        objectsRef.current.push({ type: "text", x: cx - 30, y: cy, text: type.split("|")[1] });
      }
      redraw();
    };

    const getPos = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return [(clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY];
    };

    const onDragOver = (e) => e.preventDefault();
    const onDrop = (e) => {
      e.preventDefault();
      const data = e.dataTransfer.getData("text/plain");
      const [x, y] = getPos(e.clientX, e.clientY);
      if (data === "vetrata" || data === "muro") {
        objectsRef.current.push({
          type: "line",
          x1: x,
          y1: y,
          x2: x + 60,
          y2: y,
          color: data === "vetrata" ? "#00bfff" : "#8b5a2b",
        });
      } else if (data.startsWith("vano|")) {
        objectsRef.current.push({ type: "text", x, y, text: data.split("|")[1] });
      }
      redraw();
    };
    const pointerDown = (mx, my) => {
      selectedRef.current = null;
      dragModeRef.current = null;
      selectedIndexRef.current = -1;
      const objects = objectsRef.current;
      for (let i = objects.length - 1; i >= 0; i--) {
        const o = objects[i];
        if (o.type === "line") {
          if (distance(mx, my, o.x1, o.y1) < 12) {
            selectedRef.current = o;
            selectedIndexRef.current = i;
            dragModeRef.current = "start";
            break;
          }
          if (distance(mx, my, o.x2, o.y2) < 12) {
            selectedRef.current = o;
            selectedIndexRef.current = i;
            dragModeRef.current = "end";
            break;
          }
          if (distancePointToLine(mx, my, o) < 8) {
            selectedRef.current = o;
            selectedIndexRef.current = i;
            dragModeRef.current = "move";
            break;
          }
        } else if (o.type === "text") {
          if (mx > o.x - 4 && mx < o.x + 64 && my > o.y - 18 && my < o.y + 6) {
            selectedRef.current = o;
            selectedIndexRef.current = i;
            dragModeRef.current = "move";
            break;
          }
        }
      }
      redraw();
    };
    const pointerMove = (mx, my) => {
      const selected = selectedRef.current;
      if (!selected) return;
      const dragMode = dragModeRef.current;
      if (selected.type === "line") {
        if (dragMode === "start") {
          selected.x1 = mx;
          selected.y1 = my;
        }
        if (dragMode === "end") {
          selected.x2 = mx;
          selected.y2 = my;
        }
        if (dragMode === "move") {
          const dx = mx - selected.x1;
          const dy = my - selected.y1;
          selected.x2 += dx;
          selected.y2 += dy;
          selected.x1 = mx;
          selected.y1 = my;
        }
      }
      if (selected.type === "text" && dragMode === "move") {
        selected.x = mx;
        selected.y = my;
      }
      redraw();
    };
    const pointerUp = () => {
      dragModeRef.current = null;
    };

    const onMouseDown = (e) => {
      const [mx, my] = getPos(e.clientX, e.clientY);
      pointerDown(mx, my);
    };
    const onMouseMove = (e) => {
      if (!selectedRef.current) return;
      const [mx, my] = getPos(e.clientX, e.clientY);
      pointerMove(mx, my);
    };
    const onMouseUp = () => pointerUp();

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      const [mx, my] = getPos(t.clientX, t.clientY);
      pointerDown(mx, my);
      if (selectedRef.current) e.preventDefault();
    };
    const onTouchMove = (e) => {
      if (!selectedRef.current || !e.touches || e.touches.length !== 1) return;
      e.preventDefault();
      const t = e.touches[0];
      const [mx, my] = getPos(t.clientX, t.clientY);
      pointerMove(mx, my);
    };
    const onTouchEnd = () => pointerUp();

    canvas.addEventListener("dragover", onDragOver);
    canvas.addEventListener("drop", onDrop);
    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", onTouchEnd);
    redraw();

    return () => {
      canvas.removeEventListener("dragover", onDragOver);
      canvas.removeEventListener("drop", onDrop);
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", onMouseUp);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);
    };
  }, [step]);

  const dragStart = (e, payload) => {
    e.dataTransfer.setData("text/plain", payload);
  };

  // ---------- Actions ----------
  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const apriConfiguratore = () => {
    if (!lead.ragione_sociale.trim()) return toast.error("Inserisci la Ragione Sociale");
    if (!validEmail(lead.email)) return toast.error("Inserisci un'email valida");
    if (!lead.telefono.trim()) return toast.error("Inserisci un recapito telefonico");
    setConfirmOpen(true);
  };

  const confirmEmailYes = async () => {
    setConfirmOpen(false);
    setOpeningConf(true);
    try {
      await axios.post(`${API}/configuratore/step1`, lead, { timeout: 15000 });
    } catch (e) {
      // Non-blocking: proceed even if notification fails
    }
    setOpeningConf(false);
    setStep(2);
    window.scrollTo(0, 0);
  };

  const confirmEmailNo = () => {
    setConfirmOpen(false);
    setLead((prev) => ({ ...prev, email: "" }));
    setTimeout(() => emailRef.current && emailRef.current.focus(), 100);
  };

  const prezzoMq = () => PREZZI_SPAZIO[tipoSpazio] || 0;

  const generaEInvia = async () => {
    if (!tipoSpazio) return toast.error("Seleziona il Tipo Spazio");
    for (const v of vani) {
      const l = parseFloat(v.lunghezza);
      const a = parseFloat(v.altezza);
      if (!l || !a || l <= 0 || a <= 0) {
        return toast.error(`Compila Lunghezza e Altezza per tutti i vani (Vano ${v.numero})`);
      }
    }
    const totaleMq = vani.reduce((s, v) => s + parseFloat(v.lunghezza) * parseFloat(v.altezza), 0);
    const base = totaleMq * prezzoMq();
    const prezzoMin = Math.round(base / 10) * 10;
    const prezzoMax = Math.round((base * 1.2) / 10) * 10;

    setSending(true);
    let canvasImage = null;
    try {
      canvasImage = canvasRef.current ? canvasRef.current.toDataURL("image/png") : null;
    } catch (e) {
      canvasImage = null;
    }
    const payload = {
      ...lead,
      ...dettagli,
      tipo_spazio: tipoSpazio,
      piano,
      numero_vani: vani.length,
      vani: vani.map((v) => ({
        numero: v.numero,
        lunghezza: parseFloat(v.lunghezza),
        altezza: parseFloat(v.altezza),
      })),
      prezzo_min: prezzoMin,
      prezzo_max: prezzoMax,
      totale_mq: totaleMq,
      canvas_image: canvasImage,
    };
    try {
      const res = await axios.post(`${API}/configuratore/submit`, payload, { timeout: 40000 });
      setPreventivoNr(res.data?.preventivo_nr || "");
      setSent(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error("Errore nell'invio del preventivo. Riprova.");
    } finally {
      setSending(false);
    }
  };

  const totaleMqLive = vani.reduce((s, v) => {
    const l = parseFloat(v.lunghezza);
    const a = parseFloat(v.altezza);
    return s + (l > 0 && a > 0 ? l * a : 0);
  }, 0);

  return (
    <div data-testid="configuratore-page" className="min-h-screen bg-background">
      <Seo
        title="Configuratore Tecnico-Commerciale VEPA | VETRO DESIGN ITALIA"
        description="Configura la tua vetrata panoramica VEPA e ottieni una stima immediata. Disegna la struttura e ricevi il preventivo tecnico via email."
        path="/configuratore"
      />
      <Navbar />

      <div className="max-w-[1100px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-24">
        {sent ? (
          <div data-testid="conf-success" className="max-w-2xl mx-auto text-center py-16">
            <CheckCircle2 className="w-16 h-16 mx-auto text-teal-600" strokeWidth={1.2} />
            <h1 className="font-serif font-light text-3xl md:text-4xl mt-6 text-[#26241f]">
              Preventivo inviato correttamente a VETRO DESIGN ITALIA S.r.L.
            </h1>
            {preventivoNr && (
              <p data-testid="conf-success-nr" className="mt-4 text-lg text-teal-700 font-medium">
                Preventivo Tecnico-Commerciale — Configuratore Nr. {preventivoNr}
              </p>
            )}
            <p className="mt-4 text-foreground/70">
              Verrete contattati nel più breve tempo possibile.
            </p>
          </div>
        ) : (
          <>
            {/* Progress */}
            <div className="flex items-center justify-center gap-3 mb-10">
              <span className={`h-1 w-16 transition-colors ${step >= 1 ? "bg-teal-600" : "bg-black/15"}`} />
              <span className={`h-1 w-16 transition-colors ${step >= 2 ? "bg-teal-600" : "bg-black/15"}`} />
            </div>

            {step === 1 && (
              <div data-testid="conf-step1" className="max-w-2xl mx-auto">
                <p className="text-xs uppercase tracking-[0.3em] text-teal-700 text-center">Passo 1 di 2</p>
                <h1 className="font-serif font-light text-3xl md:text-5xl text-center mt-3 text-[#26241f]">
                  Configuratore Tecnico-Commerciale 1/2
                </h1>
                <p className="mt-6 text-center text-foreground/70 leading-relaxed">
                  Ti chiediamo cortesemente di inserire i tuoi dati con attenzione: ci permetteranno di
                  ricontattarti e inviarti il preventivo personalizzato per la tua vetrata panoramica.
                </p>

                <div className="mt-10 space-y-5">
                  <div>
                    <label className="block text-sm text-foreground/70 mb-2">Ragione Sociale *</label>
                    <input
                      data-testid="conf-ragione-sociale"
                      value={lead.ragione_sociale}
                      onChange={(e) => setLead({ ...lead, ragione_sociale: e.target.value })}
                      className="w-full bg-white border border-black/15 px-4 py-3 focus:outline-none focus:border-teal-600 transition-colors"
                      placeholder="Nome / Azienda"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-foreground/70 mb-2">Email *</label>
                      <input
                        data-testid="conf-email"
                        ref={emailRef}
                        type="email"
                        value={lead.email}
                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        className="w-full bg-white border border-black/15 px-4 py-3 focus:outline-none focus:border-teal-600 transition-colors"
                        placeholder="email@esempio.it"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-foreground/70 mb-2">Telefono *</label>
                      <input
                        data-testid="conf-telefono"
                        value={lead.telefono}
                        onChange={(e) => setLead({ ...lead, telefono: e.target.value })}
                        className="w-full bg-white border border-black/15 px-4 py-3 focus:outline-none focus:border-teal-600 transition-colors"
                        placeholder="+39 ..."
                      />
                    </div>
                  </div>

                  <button
                    data-testid="conf-apri-configuratore"
                    onClick={apriConfiguratore}
                    disabled={openingConf}
                    className="w-full inline-flex items-center justify-center gap-3 bg-teal-600 text-white px-8 py-4 text-sm uppercase tracking-wide hover:bg-teal-700 transition-colors disabled:opacity-60"
                  >
                    {openingConf ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Apri Configuratore
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="conf-step2">
                <p className="text-xs uppercase tracking-[0.3em] text-teal-700 text-center">Passo 2 di 2</p>
                <h1 className="font-serif font-light text-3xl md:text-5xl text-center mt-3 text-[#26241f]">
                  Configuratore Tecnico-Commerciale 2/2
                </h1>

                {/* Inputs */}
                <div className="mt-10 grid md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-sm text-foreground/70 mb-2">Tipo di spazio da chiudere *</label>
                    <select
                      data-testid="conf-tipo-spazio"
                      value={tipoSpazio}
                      onChange={(e) => {
                        setTipoSpazio(e.target.value);
                      }}
                      className="w-full bg-white border border-black/15 px-4 py-3 focus:outline-none focus:border-teal-600 transition-colors"
                    >
                      <option value="">Seleziona...</option>
                      {TIPI_SPAZIO_GROUPS.map((g) => (
                        <optgroup key={g.group} label={g.group}>
                          {g.items.map((it) => (
                            <option key={it.label} value={it.label}>
                              {it.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-foreground/70 mb-2">Piano</label>
                    <input
                      data-testid="conf-piano"
                      value={piano}
                      onChange={(e) => setPiano(e.target.value)}
                      className="w-full bg-white border border-black/15 px-4 py-3 focus:outline-none focus:border-teal-600 transition-colors"
                      placeholder="Es. Piano terra"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-foreground/70 mb-2">Numero Vani *</label>
                    <input
                      data-testid="conf-numero-vani"
                      type="number"
                      min={1}
                      max={50}
                      value={numeroVani}
                      onChange={(e) => setNumeroVani(e.target.value)}
                      className="w-full bg-white border border-black/15 px-4 py-3 focus:outline-none focus:border-teal-600 transition-colors"
                    />
                  </div>
                </div>

                {/* Vani table */}
                <div className="mt-8">
                  <h2 className="text-sm uppercase tracking-wide text-[#26241f] mb-3">Dimensioni Vani (metri)</h2>
                  <div className="overflow-x-auto border border-black/10 bg-white">
                    <table className="w-full text-sm" data-testid="conf-vani-table">
                      <thead>
                        <tr className="bg-[#26241f] text-white">
                          <th className="text-left px-4 py-3 font-light">Vano</th>
                          <th className="text-left px-4 py-3 font-light">Lunghezza (m)</th>
                          <th className="text-left px-4 py-3 font-light">Altezza (m)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vani.map((v, i) => (
                          <tr key={i} className="border-t border-black/5">
                            <td className="px-4 py-2 font-medium">{v.numero}</td>
                            <td className="px-4 py-2">
                              <input
                                data-testid={`conf-vano-${v.numero}-lunghezza`}
                                type="number"
                                step="0.01"
                                min="0"
                                value={v.lunghezza}
                                onChange={(e) => {
                                  const nv = [...vani];
                                  nv[i] = { ...nv[i], lunghezza: e.target.value };
                                  setVani(nv);
                                }}
                                className="w-32 border border-black/15 px-3 py-2 focus:outline-none focus:border-teal-600"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                data-testid={`conf-vano-${v.numero}-altezza`}
                                type="number"
                                step="0.01"
                                min="0"
                                value={v.altezza}
                                onChange={(e) => {
                                  const nv = [...vani];
                                  nv[i] = { ...nv[i], altezza: e.target.value };
                                  setVani(nv);
                                }}
                                className="w-32 border border-black/15 px-3 py-2 focus:outline-none focus:border-teal-600"
                                placeholder="0.00"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Dettagli tecnici (facoltativi) */}
                <div className="mt-8">
                  <h2 className="text-sm uppercase tracking-wide text-[#26241f] mb-3">Dettagli Tecnici (facoltativi)</h2>
                  <div className="grid md:grid-cols-2 gap-5">
                    {DETTAGLI_TECNICI.map((f) => (
                      <div key={f.key}>
                        <label className="block text-sm text-foreground/70 mb-2">{f.label}</label>
                        <select
                          data-testid={`conf-${f.key}`}
                          value={dettagli[f.key]}
                          onChange={(e) => setDettagli({ ...dettagli, [f.key]: e.target.value })}
                          className="w-full bg-white border border-black/15 px-4 py-3 focus:outline-none focus:border-teal-600 transition-colors"
                        >
                          <option value="">— Seleziona (facoltativo) —</option>
                          {f.options.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Canvas */}
                <div className="mt-8">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
                    <h2 className="text-sm uppercase tracking-wide text-[#26241f]">Disegno Struttura</h2>
                    <button
                      type="button"
                      data-testid="conf-video-tutorial-btn"
                      onClick={() => setVideoOpen(true)}
                      className="inline-flex items-center gap-2 text-sm text-teal-700 border border-teal-600/40 rounded-full px-3 py-1 hover:bg-teal-50 transition-colors"
                    >
                      <PlayCircle className="w-4 h-4" strokeWidth={1.6} /> Guarda il video tutorial
                    </button>
                  </div>
                  <p className="text-sm text-foreground/70 leading-relaxed mb-4 max-w-3xl" data-testid="conf-disegno-intro">
                    Per aiutarci a comprendere la configurazione del tuo spazio, ti chiediamo di realizzare un semplice
                    disegno. Seleziona gli elementi disponibili (linea azzurra = vetrata, linea marrone = muri/struttura
                    esistente) e trascinali all'interno della griglia per riprodurre il perimetro dell'area da chiudere.
                    <br />
                    Ogni elemento può essere spostato, allungato, accorciato e orientato in base alle caratteristiche del
                    tuo spazio.
                    <br />
                    <span className="text-teal-700">
                      Su smartphone e tablet: <strong>tocca</strong> un elemento qui sotto per aggiungerlo alla griglia,
                      poi trascinalo con il dito per spostarlo o ridimensionarlo.
                    </span>
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div
                      data-testid="palette-vetrata"
                      draggable
                      onDragStart={(e) => dragStart(e, "vetrata")}
                      onClick={() => addElementRef.current("vetrata")}
                      title="Tocca o trascina per aggiungere"
                      className="flex items-center gap-2 border border-black/15 bg-white px-4 py-2 cursor-pointer select-none active:bg-black/5"
                    >
                      <span className="inline-block w-6 h-1.5" style={{ background: "#00bfff" }} />
                      <span className="text-sm">Vetrata</span>
                    </div>
                    <div
                      data-testid="palette-muro"
                      draggable
                      onDragStart={(e) => dragStart(e, "muro")}
                      onClick={() => addElementRef.current("muro")}
                      title="Tocca o trascina per aggiungere"
                      className="flex items-center gap-2 border border-black/15 bg-white px-4 py-2 cursor-pointer select-none active:bg-black/5"
                    >
                      <span className="inline-block w-6 h-1.5" style={{ background: "#8b5a2b" }} />
                      <span className="text-sm">Muro</span>
                    </div>
                    {vani.map((v) => (
                      <div
                        key={v.numero}
                        data-testid={`palette-vano-${v.numero}`}
                        draggable
                        onDragStart={(e) => dragStart(e, `vano|Vano ${v.numero}`)}
                        onClick={() => addElementRef.current(`vano|Vano ${v.numero}`)}
                        title="Tocca o trascina per aggiungere"
                        className="flex items-center gap-2 border border-dashed border-black/25 bg-white px-3 py-2 cursor-pointer select-none active:bg-black/5 text-sm"
                      >
                        Vano {v.numero}
                      </div>
                    ))}
                  </div>
                  <div className="border border-black/10 bg-white max-w-full">
                    <canvas
                      ref={canvasRef}
                      data-testid="conf-canvas"
                      width={CANVAS_W}
                      height={CANVAS_H}
                      style={{ width: "100%", height: "auto", display: "block", touchAction: "none" }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-3 mt-4">
                    <button
                      data-testid="conf-delete-selected"
                      onClick={() => deleteSelectedRef.current()}
                      className="inline-flex items-center gap-2 border border-black/20 px-4 py-2 text-sm hover:bg-black/5 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.4} /> Elimina selezione
                    </button>
                    <button
                      data-testid="conf-clear-canvas"
                      onClick={() => clearCanvasRef.current()}
                      className="inline-flex items-center gap-2 border border-black/20 px-4 py-2 text-sm hover:bg-black/5 transition-colors"
                    >
                      <Eraser className="w-4 h-4" strokeWidth={1.4} /> Pulisci disegno
                    </button>
                  </div>
                  <p className="text-xs text-foreground/50 mt-2">
                    Trascina "Vetrata" o "Muro" sulla griglia. Clicca un elemento per selezionarlo, trascina gli
                    estremi per ridimensionare/ruotare, il centro per spostarlo.
                  </p>
                </div>

                {/* Note */}
                <div className="mt-8">
                  <label className="block text-sm text-foreground/70 mb-2">Note</label>
                  <textarea
                    data-testid="conf-note"
                    rows={4}
                    value={lead.note}
                    onChange={(e) => setLead({ ...lead, note: e.target.value })}
                    className="w-full bg-white border border-black/15 px-4 py-3 focus:outline-none focus:border-teal-600 transition-colors resize-none"
                    placeholder="Descrivi brevemente il tuo progetto..."
                  />
                </div>

                {/* Info */}
                <div data-testid="conf-info" className="mt-8 border border-teal-600/30 bg-teal-50 p-6 text-center">
                  <p className="text-2xl font-serif text-teal-800">
                    Superficie totale: {totaleMqLive.toFixed(2)} mq
                  </p>
                  <p className="mt-3 text-lg text-[#26241f]">
                    Clicca su “GENERA E INVIA PREVENTIVO” per ricevere il preventivo per email
                  </p>
                  <p className="text-xs text-foreground/50 mt-3">
                    Stima orientativa, IVA esclusa. Il preventivo definitivo verrà redatto dopo sopralluogo tecnico.
                  </p>
                </div>

                {/* Actions */}
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <button
                    data-testid="conf-back"
                    onClick={() => setStep(1)}
                    className="inline-flex items-center justify-center gap-2 border border-black/20 px-6 py-4 text-sm uppercase tracking-wide hover:bg-black/5 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.4} /> Indietro
                  </button>
                  <button
                    data-testid="conf-genera-invia-preventivo"
                    onClick={generaEInvia}
                    disabled={sending}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-8 py-4 text-sm uppercase tracking-wide hover:bg-teal-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.4} />}
                    Genera e Invia Preventivo
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="conf-email-confirm-modal">
          <DialogHeader>
            <DialogTitle>Conferma l'email</DialogTitle>
          </DialogHeader>
          <p className="text-foreground/80 break-words">
            È corretta l'email <strong className="text-[#26241f]">{lead.email}</strong>?
          </p>
          <div className="flex gap-3 justify-end mt-4">
            <button
              data-testid="conf-email-confirm-no"
              onClick={confirmEmailNo}
              className="inline-flex items-center justify-center border border-black/20 px-6 py-2.5 text-sm uppercase tracking-wide hover:bg-black/5 transition-colors"
            >
              No
            </button>
            <button
              data-testid="conf-email-confirm-ok"
              onClick={confirmEmailYes}
              className="inline-flex items-center justify-center bg-teal-600 text-white px-6 py-2.5 text-sm uppercase tracking-wide hover:bg-teal-700 transition-colors"
            >
              Ok
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="max-w-2xl" data-testid="conf-video-modal">
          <DialogHeader>
            <DialogTitle>Come realizzare il disegno della struttura</DialogTitle>
          </DialogHeader>
          <video
            data-testid="conf-tutorial-video"
            controls
            preload="none"
            playsInline
            className="w-full rounded-md bg-black"
            src={`${process.env.PUBLIC_URL || ""}/videos/tutorial-disegno.mp4`}
          />
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
